import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { useTranslation } from 'react-i18next';
import { SkeletonLine } from 'components/theme/Skeleton/Skeleton';
import { TruncatedText, TruncationMode } from 'components/TruncatedText';
import Tabs from 'components/Tabs/Tabs';
import { CancellationPolicy } from 'types/listings';
import ChipList from 'components/ChipList';
import Chip from 'components/Chip/Chip';
import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import { HyattExperienceBenefits } from 'components/molecules/HyattExperienceBenefits';
import { PublicListingDetailsV3 } from 'types/listingV3';

interface TabsInfo {
  labels: string[];
  contents: (string | string[])[];
}

const getTabsForEvent = (
  translate: (s: string) => string,
  listing?: PublicListingDetailsV3,
  cancellationPolicy?: CancellationPolicy,
): TabsInfo => {
  const tabs: TabsInfo = {
    labels: [],
    contents: [],
  };

  if (listing?.description) {
    tabs.labels.push(translate('about'));
    tabs.contents.push(listing.description);
  }
  if (listing?.agenda) {
    tabs.labels.push(translate('agenda'));
    tabs.contents.push(listing.agenda);
  }
  if (listing?.menu) {
    tabs.labels.push(translate('menu'));
    tabs.contents.push(listing.menu);
  }
  if (
    listing?.resourceGroups?.[0]?.included &&
    listing?.resourceGroups?.[0]?.included.length > 0
  ) {
    tabs.labels.push(translate('included'));
    tabs.contents.push(listing?.resourceGroups?.[0]?.included);
  }
  if (cancellationPolicy && cancellationPolicy.active) {
    tabs.labels.push(translate('cancellationPolicy'));
    tabs.contents.push(`<p>${cancellationPolicy.notes}</p>`);
  }

  return tabs;
};

interface EventDetailsProps {
  hyattEligible?: {
    payEligible?: boolean;
    earnEligible?: boolean;
  };
}

const EventDetails = ({ hyattEligible }: EventDetailsProps) => {
  const { isLoading, listing, cancellationPolicy } = useEventBookingSession();
  const { t: translate } = useTranslation();
  const tabs = useMemo(
    () => getTabsForEvent(translate, listing, cancellationPolicy),
    [listing, cancellationPolicy, translate],
  );
  const [showMore, setShowMore] = useState(false);

  const vibesEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.ENABLE_VIBES_V2,
  );

  if (isLoading || !listing) {
    return (
      <Wrapper>
        <HeaderSkeleton />
        <TabsContentSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {vibesEnabled && (
        <StyledChipList>
          {listing.vibes.map((vibe) => (
            <Chip key={vibe.id} className="listing-card-vibe">
              {vibe.name}
            </Chip>
          ))}
        </StyledChipList>
      )}
      {hyattEligible && (
        <HyattExperienceBenefits
          payEligible={hyattEligible.payEligible}
          earnEligible={hyattEligible.earnEligible}
        />
      )}

      <Tabs labelAs="h3" tabsLabel={tabs.labels} asBtn>
        {tabs.contents.map((content) => {
          if (typeof content === 'string') {
            return (
              <TruncatedText
                mode={TruncationMode.INLINE}
                text={content}
                maxLines={showMore ? Infinity : 300}
                isRichText
                onShowMore={() => setShowMore(true)}
                showLessText={translate('viewLess')}
                showMoreText={translate('viewMore')}
                textBottomGap={8}
                customStyles={customTextStyles}
              />
            );
          }

          return (
            <IncludedItemsWrapper>
              {content.map((item) => (
                <IncludedItem className="acitvate-included-item" key={item}>
                  {item}
                </IncludedItem>
              ))}
            </IncludedItemsWrapper>
          );
        })}
      </Tabs>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  padding-top: 24px;
  padding-bottom: 32px;
  border-bottom: solid 0.5px var(--way-colors-borderColor);
`;

const StyledChipList = styled(ChipList)`
  margin-bottom: 48px;
`;

const customTextStyles = css`
  & > p {
    font-size: 16px;
    line-height: 18px;
    color: var(--way-colors-secondaryTextColor);
  }
`;

const IncludedItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const IncludedItem = styled.span`
  user-select: none;
  padding: 8px 12px;
  border: 0.5px solid var(--way-colors-borderColor);
  border-radius: 0;
  font-weight: 900;
  color: var(--way-colors-contrastColorShades-60);
`;

const HeaderSkeleton = styled(SkeletonLine)`
  height: 24px;
  margin-bottom: 16px;
`;

const TabsContentSkeleton = styled(SkeletonLine)`
  height: 120px;
`;

export default EventDetails;
